import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut,
  createUserWithEmailAndPassword,
  updatePassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, setDoc, deleteDoc, query, where, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign in function
  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Sign out function
  const logout = () => {
    return signOut(auth);
  };

  // Create staff account (Admin only)
  const createStaffAccount = async (email, password, userData) => {
    try {
      // Create a temporary Firebase app instance for user creation
      const tempApp = initializeApp({
        apiKey: auth.app.options.apiKey,
        authDomain: auth.app.options.authDomain,
        projectId: auth.app.options.projectId,
        storageBucket: auth.app.options.storageBucket,
        messagingSenderId: auth.app.options.messagingSenderId,
        appId: auth.app.options.appId
      }, 'tempApp' + Date.now());
      
      const tempAuth = getAuth(tempApp);
      
      // Create the new staff user using the temporary auth instance
      const userCredential = await createUserWithEmailAndPassword(tempAuth, email, password);
      const newUser = userCredential.user;
      
      // Save user role and data to Firestore using the main db instance
      await setDoc(doc(db, 'users', newUser.uid), {
        email: newUser.email,
        role: 'staff',
        createdAt: new Date(),
        ...userData
      });

      // Delete the temporary app to clean up
      await deleteApp(tempApp);
      
      // Return success - admin stays signed in!
      return {
        success: true,
        message: 'Staff account created successfully!',
        requiresReauth: false
      };
      
    } catch (error) {
      // If email already in use, provide helpful guidance
      if (error.code === 'auth/email-already-in-use') {
        throw new Error(`The email ${email} is already registered in Firebase. This means:\n\n1. If this was a deleted staff member, they should be able to log in with their existing password\n2. If you need to change their details, they can be updated after they log in\n3. If you forgot their password, please use Firebase Console to reset it\n\nNo new account was created.`);
      }
      throw error;
    }
  };

  // Delete staff account completely (Admin only)
  const deleteStaffAccount = async (staffId, staffEmail) => {
    try {
      // Delete the Firestore document completely
      await deleteDoc(doc(db, 'users', staffId));
      
      return {
        success: true,
        message: `Staff account deleted completely!\n\nFirestore document: ✅ Deleted\nFirebase Auth account: ⚠️ Still exists\n\nTo reuse the email "${staffEmail}", you need to:\n1. Go to Firebase Console → Authentication → Users\n2. Find "${staffEmail}" and delete the account\n3. Then you can create a new account with the same email`,
        firestoreDeleted: true,
        authDeleted: false
      };
      
    } catch (error) {
      throw error;
    }
  };

  // Simple staff account creation - no reactivation complexity
  const createStaffAccountSimple = async (email, password, userData) => {
    try {
      // Just create the staff account - if email exists, Firebase will tell us
      return await createStaffAccount(email, password, userData);
    } catch (error) {
      throw error;
    }
  };

  // Get user role from Firestore
  const getUserRole = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const role = userDoc.data().role;
        // Block access for inactive users
        if (role === 'inactive') {
          await signOut(auth);
          throw new Error('Account has been deactivated. Please contact your administrator.');
        }
        return role;
      }
      return null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setCurrentUser(user);
          const role = await getUserRole(user.uid);
          setUserRole(role);
        } catch (error) {
          // If getUserRole throws an error (like inactive account), handle it
          console.error('Error setting user role:', error);
          setCurrentUser(null);
          setUserRole(null);
          // The user will be signed out by getUserRole if inactive
        }
      } else {
        setCurrentUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    login,
    logout,
    createStaffAccount,
    deleteStaffAccount,
    createStaffAccountSimple,
    isAdmin: userRole === 'admin',
    isStaff: userRole === 'staff',
    canAccessModule: (module) => {
      if (userRole === 'admin') return true;
      if (userRole === 'staff') {
        // Staff cannot access expenses, investments, and staff management
        return !['expenses', 'investments', 'staff'].includes(module);
      }
      return false;
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
